import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { chooseReturnTo } from '../utils/authNavigation';
import '../styles/Login.css';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckroomIcon from '@mui/icons-material/CheckroomRounded';
import ChatIcon from '@mui/icons-material/ChatBubbleRounded';
import GroupsIcon from '@mui/icons-material/Groups2Rounded';
import VerifiedIcon from '@mui/icons-material/VerifiedRounded';

const Login = () => {
  const {
    isAuthenticated,
    processLoginCallback,
    initializeGoogleSignIn,
    gsiScriptLoaded,
    error: authError,
  } = useContext(AuthContext);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const googleButtonRef = useRef(null);
  const reason = new URLSearchParams(location.search).get('reason');
  const statePath = location.state?.from
    ? `${location.state.from.pathname || ''}${location.state.from.search || ''}`
    : null;
  const storedPath = sessionStorage.getItem('fashionmate_return_to');
  const returnTo = chooseReturnTo(statePath, storedPath, '/');

  useEffect(() => {
    if (isAuthenticated) {
      sessionStorage.removeItem('fashionmate_return_to');
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    const callbackParams = new URLSearchParams(location.search);
    const errorParam = callbackParams.get('error');
    if (errorParam) {
      setError('Authentication failed. Please try again.');
    } else if (reason === 'session-expired') {
      setError('Your session expired. Sign in again to continue where you left off.');
    }

    const token = callbackParams.get('token');
    if (token) {
      setLoading(true);
      processLoginCallback(token)
        .then((success) => {
          if (success) navigate(returnTo, { replace: true });
          else setError('Invalid login session.');
        })
        .finally(() => setLoading(false));
    }
  }, [location.search, processLoginCallback, navigate, reason, returnTo]);

  useEffect(() => {
    if (gsiScriptLoaded && googleButtonRef.current) {
      initializeGoogleSignIn('google-signin-button');
    }
  }, [gsiScriptLoaded, initializeGoogleSignIn]);

  const productFeatures = [
    {
      icon: <CheckroomIcon />,
      title: 'Smart Closet',
      description: 'Digitize your wardrobe and find what actually works together.',
    },
    {
      icon: <ChatIcon />,
      title: 'AI Stylist',
      description: 'Get instant outfit direction for weather, events, and mood.',
    },
    {
      icon: <AutoAwesomeIcon />,
      title: 'Style Memory',
      description: 'Track your outfit history and improve your personal style system.',
    },
  ];

  if (loading) {
    return (
      <div className="login-shell">
        <div className="login-orb orb-left" />
        <div className="login-orb orb-right" />
        <div className="login-loading-card glass-panel-heavy">
          <div className="loading-spinner" />
          <h3>Logging you in</h3>
          <p>We are securing your session and loading your workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-orb orb-left" />
      <div className="login-orb orb-right" />
      <div className="login-grid-pattern" />

      <header className="login-topbar">
        <div className="login-wordmark">
          <span className="wordmark-badge">FM</span>
          <div>
            <p className="wordmark-title">FashionMate AI</p>
            <p className="wordmark-subtitle">Personal styling workspace</p>
          </div>
        </div>
      </header>

      <main className="login-stage">
        <section className="login-story animate-slide-up">
          <span className="story-eyebrow">Your wardrobe command center</span>
          <h1 className="story-title">Make getting dressed feel effortless.</h1>
          <p className="story-description">
            FashionMate helps you organize every piece, plan better outfits, and get AI guidance that matches your real style.
          </p>

          <div className="story-feature-list">
            {productFeatures.map((feature) => (
              <article key={feature.title} className="story-feature-card">
                <div className="story-feature-icon">{feature.icon}</div>
                <div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </article>
            ))}
          </div>

        </section>

        <section className="login-panel glass-panel-heavy animate-fade-in">
          <div className="panel-header">
            <h2>Welcome back</h2>
            <p>Sign in with Google to access your FashionMate account.</p>
          </div>

          {(error || authError) && <div className="panel-error">{error || authError}</div>}

          <div className="auth-button-wrapper">
            <div id="google-signin-button" className="google-btn-custom" ref={googleButtonRef} />
          </div>

          <div className="panel-meta">
            <div className="meta-item">
              <VerifiedIcon fontSize="small" />
              <span>Secure Google authentication</span>
            </div>
            <div className="meta-item">
              <GroupsIcon fontSize="small" />
              <span>Built for personal and collaborative styling</span>
            </div>
          </div>

          <p className="panel-footer-copy">
            By continuing, you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Login;
